/* ═══════════════════════════════════════════════════════
   src/content/domains/cards/valor.tsx
   All Valor domain cards with text pre-rendered as JSX.
   Edit `rendered` to update display. Edit `text` to update search.
   ═══════════════════════════════════════════════════════ */

import { DomainCard } from './types';
import {
  HOPE,
  STRESS,
  VERY_CLOSE,
  CLOSE,
  FAR,
  MELEE,
  VULNERABLE,
  ARMOR_SLOT,
  ARMOR_SLOTS,
  HIT_POINTS,
  HIT_POINT,
  LOADOUT,
  VAULT,
  DISADVANTAGE,
  LONG_REST,
} from '../../templates';

const valorCards: DomainCard[] = [
  {
    name: "Bare Bones",
    level: 1,
    domain: "Valor",
    type: "Ability",
    recallCost: 0,
    text: "When you choose not to equip armor, you have a base Armor Score of 3 + your Strength and use the following as your base damage thresholds:\n\n- Tier 1: 9/19\n- Tier 2: 11/24\n- Tier 3: 13/31\n- Tier 4: 15/38",
    rendered: (
      <>
        <p key={0} className="card-text-para">When you choose not to equip armor, you have a base Armor Score of 3 + your Strength and use the following as your base damage thresholds:</p>
        <div key={1} className="card-text-gap" />
        <div key={2} className="card-text-bullet"><span className="card-text-bullet__arrow">•</span><span><em>Tier 1</em>: 9/19</span></div>
        <div key={3} className="card-text-bullet"><span className="card-text-bullet__arrow">•</span><span><em>Tier 2</em>: 11/24</span></div>
        <div key={4} className="card-text-bullet"><span className="card-text-bullet__arrow">•</span><span><em>Tier 3</em>: 13/31</span></div>
        <div key={5} className="card-text-bullet"><span className="card-text-bullet__arrow">•</span><span><em>Tier 4</em>: 15/38</span></div>
      </>
    ),
  },
  {
    name: "Forceful Push",
    level: 1,
    domain: "Valor",
    type: "Ability",
    recallCost: 0,
    text: "Make an attack with your primary weapon against a target within Melee range. On a success, you deal damage and knock them back to Close range. On a success with Hope, add a **d6** to your damage roll.\n\nAdditionally, you can **spend a Hope** to make them temporarily Vulnerable.",
    rendered: (
      <>
        <p key={0} className="card-text-para">Make an attack with your primary weapon against a target within {MELEE} range. On a success, you deal damage and knock them back to {CLOSE} range. On a success with {HOPE}, add a <strong className="inline-strong">d6</strong> to your damage roll.</p>
        <div key={1} className="card-text-gap" />
        <p key={2} className="card-text-para">Additionally, you can <strong className="inline-strong">spend a {HOPE}</strong> to make them temporarily {VULNERABLE}.</p>
      </>
    ),
  },
  {
    name: "I Am Your Shield",
    level: 1,
    domain: "Valor",
    type: "Ability",
    recallCost: 1,
    text: "When an ally within Very Close range would take damage, you can **mark a Stress** to stand in the way and make yourself the target of the attack instead. When you take damage from this attack, you can mark any number of Armor Slots.",
    rendered: (
      <>
        <p key={0} className="card-text-para">When an ally within {VERY_CLOSE} range would take damage, you can <strong className="inline-strong">mark a {STRESS}</strong> to stand in the way and make yourself the target of the attack instead. When you take damage from this attack, you can mark any number of {ARMOR_SLOTS}.</p>
      </>
    ),
  },
  {
    name: "Body Basher",
    level: 2,
    domain: "Valor",
    type: "Ability",
    recallCost: 1,
    text: "You use the full force of your body in a fight. On a successful attack using a weapon with a Melee range, gain a bonus to your damage roll equal to your Strength.",
    rendered: (
      <>
        <p key={0} className="card-text-para">You use the full force of your body in a fight. On a successful attack using a weapon with a Melee range, gain a bonus to your damage roll equal to your Strength.</p>
      </>
    ),
  },
  {
    name: "Bold Presence",
    level: 2,
    domain: "Valor",
    type: "Ability",
    recallCost: 0,
    text: "When you make a Presence Roll, you can **spend a Hope** to add your Strength to the roll.\n\nAdditionally, once per rest when you would gain a condition, you can describe how your bold presence aids you in the situation and avoid gaining the condition.",
    rendered: (
      <>
        <p key={0} className="card-text-para">When you make a Presence Roll, you can <strong className="inline-strong">spend a {HOPE}</strong> to add your Strength to the roll.</p>
        <div key={1} className="card-text-gap" />
        <p key={2} className="card-text-para">Additionally, once per rest when you would gain a condition, you can describe how your bold presence aids you in the situation and avoid gaining the condition.</p>
      </>
    ),
  },
  {
    name: "Critical Inspiration",
    level: 3,
    domain: "Valor",
    type: "Ability",
    recallCost: 1,
    text: "Once per rest, when you critically succeed on an attack, all allies within Very Close range can clear a Stress or gain a Hope.",
    rendered: (
      <>
        <p key={0} className="card-text-para">Once per rest, when you critically succeed on an attack, all allies within {VERY_CLOSE} range can clear a {STRESS} or gain a {HOPE}.</p>
      </>
    ),
  },
  {
    name: "Lean on Me",
    level: 3,
    domain: "Valor",
    type: "Ability",
    recallCost: 1,
    text: "Once per long rest, when you console or inspire an ally who failed an action roll, you can both clear 2 Stress.",
    rendered: (
      <>
        <p key={0} className="card-text-para">Once per {LONG_REST}, when you console or inspire an ally who failed an action roll, you can both clear 2 {STRESS}.</p>
      </>
    ),
  },
  {
    name: "Goad Them on",
    level: 4,
    domain: "Valor",
    type: "Ability",
    recallCost: 1,
    text: "Describe how you taunt a target within Close range, then make a **Presence Roll** against them. On a success, the target must mark a Stress, and the next time the GM spotlights them, they must target you with an attack, which they make with disadvantage.",
    rendered: (
      <>
        <p key={0} className="card-text-para">Describe how you taunt a target within {CLOSE} range, then make a <strong className="inline-strong">Presence Roll</strong> against them. On a success, the target must mark a {STRESS}, and the next time the GM spotlights them, they must target you with an attack, which they make with {DISADVANTAGE}.</p>
      </>
    ),
  },
  {
    name: "Support Tank",
    level: 4,
    domain: "Valor",
    type: "Ability",
    recallCost: 2,
    text: "When an ally within Close range fails a roll, you can **spend 2 Hope** to allow them to reroll either their Hope or Fear Die.",
    rendered: (
      <>
        <p key={0} className="card-text-para">When an ally within {CLOSE} range fails a roll, you can <strong className="inline-strong">spend 2 {HOPE}</strong> to allow them to reroll either their {HOPE} or Fear Die.</p>
      </>
    ),
  },
  {
    name: "Armorer",
    level: 5,
    domain: "Valor",
    type: "Ability",
    recallCost: 1,
    text: "While you're wearing armor, gain a +1 bonus to your Armor Score.\n\nDuring a rest, when you choose to repair your armor as a downtime move, your allies also clear an Armor Slot.",
    rendered: (
      <>
        <p key={0} className="card-text-para">While you're wearing armor, gain a +1 bonus to your Armor Score.</p>
        <div key={1} className="card-text-gap" />
        <p key={2} className="card-text-para">During a rest, when you choose to repair your armor as a downtime move, your allies also clear an {ARMOR_SLOT}.</p>
      </>
    ),
  },
  {
    name: "Rousing Strike",
    level: 5,
    domain: "Valor",
    type: "Ability",
    recallCost: 1,
    text: "Once per rest, when you critically succeed on an attack, you and all allies who can see or hear you can clear a Hit Point or **1d4** Stress.",
    rendered: (
      <>
        <p key={0} className="card-text-para">Once per rest, when you critically succeed on an attack, you and all allies who can see or hear you can clear a {HIT_POINT} or <strong className="inline-strong">1d4</strong> {STRESS}.</p>
      </>
    ),
  },
  {
    name: "Inevitable",
    level: 6,
    domain: "Valor",
    type: "Ability",
    recallCost: 1,
    text: "When you fail an action roll, your next action roll has advantage.",
    rendered: (
      <>
        <p key={0} className="card-text-para">When you fail an action roll, your next action roll has advantage.</p>
      </>
    ),
  },
  {
    name: "Rise Up",
    level: 6,
    domain: "Valor",
    type: "Ability",
    recallCost: 2,
    text: "Gain a bonus to your Severe threshold equal to your Proficiency.\n\nWhen you mark 1 or more Hit Points from an attack, clear a Stress.",
    rendered: (
      <>
        <p key={0} className="card-text-para">Gain a bonus to your Severe threshold equal to your Proficiency.</p>
        <div key={1} className="card-text-gap" />
        <p key={2} className="card-text-para">When you mark 1 or more {HIT_POINTS} from an attack, clear a {STRESS}.</p>
      </>
    ),
  },
  {
    name: "Shrug It Off",
    level: 7,
    domain: "Valor",
    type: "Ability",
    recallCost: 1,
    text: "When you would take damage, you can **mark a Stress** to reduce the severity of the damage by one threshold. When you do, roll a **d6**. On a result of 3 or lower, place this card in your vault.",
    rendered: (
      <>
        <p key={0} className="card-text-para">When you would take damage, you can <strong className="inline-strong">mark a {STRESS}</strong> to reduce the severity of the damage by one threshold. When you do, roll a <strong className="inline-strong">d6</strong>. On a result of 3 or lower, place this card in your {VAULT}.</p>
      </>
    ),
  },
  {
    name: "Valor-Touched",
    level: 7,
    domain: "Valor",
    type: "Ability",
    recallCost: 1,
    text: "When 4 or more of the domain cards in your loadout are from the Valor domain, gain the following benefits:\n\n- +1 bonus to your Armor Score\n- When you mark 1 or more Hit Points without marking an Armor Slot, clear an Armor Slot.",
    rendered: (
      <>
        <p key={0} className="card-text-para">When 4 or more of the domain cards in your {LOADOUT} are from the Valor domain, gain the following benefits:</p>
        <div key={1} className="card-text-gap" />
        <div key={2} className="card-text-bullet"><span className="card-text-bullet__arrow">•</span><span>+1 bonus to your Armor Score</span></div>
        <div key={3} className="card-text-bullet"><span className="card-text-bullet__arrow">•</span><span>When you mark 1 or more {HIT_POINTS} without marking an {ARMOR_SLOT}, clear an {ARMOR_SLOT}.</span></div>
      </>
    ),
  },
  {
    name: "Full Surge",
    level: 8,
    domain: "Valor",
    type: "Ability",
    recallCost: 1,
    text: "Once per long rest, **mark 3 Stress** to push your body to its limits. Gain a +2 bonus to all of your character traits until your next rest.",
    rendered: (
      <>
        <p key={0} className="card-text-para">Once per {LONG_REST}, <strong className="inline-strong">mark 3 {STRESS}</strong> to push your body to its limits. Gain a +2 bonus to all of your character traits until your next rest.</p>
      </>
    ),
  },
  {
    name: "Ground Pound",
    level: 8,
    domain: "Valor",
    type: "Ability",
    recallCost: 2,
    text: "**Spend 2 Hope** to strike the ground where you stand and make a **Strength Roll** against all targets within Very Close range. Targets you succeed against are thrown back to Far range and must make a Reaction Roll (17). Targets who fail take **4d10+8** damage. Targets who succeed take half damage.",
    rendered: (
      <>
        <p key={0} className="card-text-para"><strong className="inline-strong">Spend 2 {HOPE}</strong> to strike the ground where you stand and make a <strong className="inline-strong">Strength Roll</strong> against all targets within {VERY_CLOSE} range. Targets you succeed against are thrown back to {FAR} range and must make a Reaction Roll (17). Targets who fail take <strong className="inline-strong">4d10+8</strong> damage. Targets who succeed take half damage.</p>
      </>
    ),
  },
  {
    name: "Hold the Line",
    level: 9,
    domain: "Valor",
    type: "Ability",
    recallCost: 1,
    text: "Describe the defensive stance you take and **spend a Hope**. If an adversary moves within Very Close range, they're pulled into Melee range and Restrained.\n\nThis condition lasts until you move or fail a roll with Fear, or the GM spends 2 Fear on their turn to clear it.",
    rendered: (
      <>
        <p key={0} className="card-text-para">Describe the defensive stance you take and <strong className="inline-strong">spend a {HOPE}</strong>. If an adversary moves within {VERY_CLOSE} range, they're pulled into {MELEE} range and <em>Restrained</em>.</p>
        <div key={1} className="card-text-gap" />
        <p key={2} className="card-text-para">This condition lasts until you move or fail a roll with Fear, or the GM spends 2 Fear on their turn to clear it.</p>
      </>
    ),
  },
  {
    name: "Lead by Example",
    level: 9,
    domain: "Valor",
    type: "Ability",
    recallCost: 3,
    text: "When you deal damage to an adversary, you can **mark a Stress** and describe how you encourage your allies. The next PC to make an attack against that adversary can clear a Stress or gain a Hope.",
    rendered: (
      <>
        <p key={0} className="card-text-para">When you deal damage to an adversary, you can <strong className="inline-strong">mark a {STRESS}</strong> and describe how you encourage your allies. The next PC to make an attack against that adversary can clear a {STRESS} or gain a {HOPE}.</p>
      </>
    ),
  },
  {
    name: "Unbreakable",
    level: 10,
    domain: "Valor",
    type: "Ability",
    recallCost: 4,
    text: "When you mark your last Hit Point, instead of making a death move, you can roll a **d6** and clear a number of Hit Points equal to the result. Then place this card in your vault.",
    rendered: (
      <>
        <p key={0} className="card-text-para">When you mark your last {HIT_POINT}, instead of making a death move, you can roll a <strong className="inline-strong">d6</strong> and clear a number of {HIT_POINTS} equal to the result. Then place this card in your {VAULT}.</p>
      </>
    ),
  },
  {
    name: "Unyielding Armor",
    level: 10,
    domain: "Valor",
    type: "Ability",
    recallCost: 1,
    text: "When you would mark an Armor Slot, roll a number of **d6s** equal to your Proficiency. If any roll a 6, reduce the severity by one threshold without marking an Armor Slot.",
    rendered: (
      <>
        <p key={0} className="card-text-para">When you would mark an {ARMOR_SLOT}, roll a number of <strong className="inline-strong">d6s</strong> equal to your Proficiency. If any roll a 6, reduce the severity by one threshold without marking an {ARMOR_SLOT}.</p>
      </>
    ),
  },
];

export default valorCards;
